import { grammarTokens, hasCompleteGrammar, requiresSentenceGrammar } from './sentence-grammar.js';
export const SENTENCE_ANNOTATION_VERSION = '2026-09-context-v1';
const norm = (s) => s.trim().replace(/[‘’]/g, "'").replace(/\s+/g, ' ');
const posLabels = new Set(['人名', '专有名词', '名词', '动词', '助动词', '情态动词', '形容词', '副词', '代词', '人称代词', '限定词', '物主限定词', '冠词', '介词', '连词', '数词', '感叹词', '不定式标记', '缩略形式']);
const pending = /待补|待校|未知|未收录|加载中|待生成|暂无|不详/;
/** Structural validation is a release gate, not a claim of human semantic review. */
export function sentenceAnnotationIssues(sentence) {
    const issues = [];
    if (!sentence.english?.trim() || !sentence.chinese?.trim())
        issues.push({ field: '原句', message: '英文原句和中文译文均为必填' });
    if (requiresSentenceGrammar(sentence.english || '') && !hasCompleteGrammar({ english: sentence.english, structure: sentence.structure || [] }))
        issues.push({ field: 'structure', message: '语法须按原词序完整覆盖，以主语、谓语、宾语、定语、状语等句法成分标注，不能用整句或词性占位' });
    const annotation = sentence.annotation;
    if (!annotation || annotation.version !== SENTENCE_ANNOTATION_VERSION || typeof annotation.english !== 'string' || typeof annotation.chinese !== 'string' || norm(annotation.english) !== norm(sentence.english || '') || norm(annotation.chinese) !== norm(sentence.chinese || '')) {
        issues.push({ field: 'annotation', message: '缺少当前规范的逐词标注，或标注原文/译文已过期；须按当前句子重新校验' });
        return issues;
    }
    const tokens = grammarTokens(sentence.english);
    if (!Array.isArray(annotation.words) || annotation.words.length !== tokens.length) {
        issues.push({ field: 'annotation.words', message: `须提供 ${tokens.length} 个逐词标注，不能漏词、合并音标或重复单词` });
        return issues;
    }
    annotation.words.forEach((word, i) => {
        const add = (field, message) => issues.push({ field: `annotation.words[${i}].${field}`, index: i, message: `第 ${i + 1} 词 ${tokens[i]}：${message}` });
        if (!word || word.index !== i || typeof word.text !== 'string' || norm(word.text) !== tokens[i])
            add('text', '序号或原词不匹配，保留原文大小写和缩写');
        if (typeof word?.phonetic !== 'string' || !/^\/[^/\s\u4e00-\u9fff\d]{1,70}\/$/u.test(word.phonetic) || pending.test(word.phonetic))
            add('phonetic', '必须填写本词的 /IPA/ 音标，不能使用整句音标或占位文字');
        if (typeof word?.meaning !== 'string' || !word.meaning.trim() || word.meaning.length > 24 || pending.test(word.meaning) || /abbr\.|[A-Za-z]{3,}|第三人称|的缩写|Information|Resources|词典/.test(word.meaning))
            add('meaning', '填写不超过 24 字的本句中文义项，不含英文缩写展开、词形说明或词典长解释');
        if (!posLabels.has(word?.pos))
            add('pos', '填写中文词性，人名标为人名；词性不能替代词段语法');
        if (word?.pos === '人名' && typeof word.meaning === 'string' && !/人名|先生|女士/.test(word.meaning))
            add('meaning', '人名须用简短译名并标明（人名）');
    });
    return issues;
}
export function assertSentenceAnnotated(sentence) {
    const issues = sentenceAnnotationIssues(sentence);
    if (issues.length)
        throw new Error(`句子标注不符合规范，已阻止发布：${issues.map(i => i.message).join('；')}`);
}
export const SENTENCE_ANNOTATION_PROMPT = `每句必须同时生成 structure 和 annotation，不能留空等待播放时补齐。
structure 是按原词序覆盖的词段数组 {label,text,explain}，label 使用主语、谓语、宾语、宾补、表语、定语、时间状语、地点状语等句法成分；名词/动词等是词性，不是句法成分。相邻同成分词段合并，不能强行规定每句至少三段，也不能用“整句”占位。
annotation 格式：{"version":"${SENTENCE_ANNOTATION_VERSION}","english":"与原句完全相同","chinese":"与译文完全相同","words":[{"index":0,"text":"保留大小写的原词","phonetic":"/本词IPA/","meaning":"本句简短中文义项","pos":"中文词性"}]}。
words 按英文词序逐词覆盖，index 从0连续递增；标点不单列，I'm、today's、连字符复合词各为一个词。每词都有音标、语境释义、词性。释义建议2至12字、最多24字，只取本句义项，不罗列字典所有含义。人名如Eric写“埃里克（人名）”，不能解释成ERIC机构缩写；is making a model写“正在/制作/一个/模型”，clean energy写“清洁的/能源”；普通复数不能解释成同拼写机构缩写。音标必须对应本词词形、重音及读音，不得整句挤在某个词下。
生成后自检每个义项与整句译文、词性及句法角色一致；有歧义须按真实语境判断，不能用待补充/未知/整句等填空。`;
